import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Send, Sparkles } from "lucide-react";

import api, { formatApiErrorDetail } from "@/lib/api";

export default function WeekAheadDigest() {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [previewData, setPreviewData] = useState(null);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [sendBusy, setSendBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    api.get("/users/directory").then((r) => {
      setUsers(r.data);
      const first = r.data.find(
        (u) => u.email === "employee@patriachurch.org",
      ) || r.data[0];
      if (first) setSelectedUserId(first.id);
    }).catch(() => {});
  }, []);

  const doPreview = async () => {
    if (!selectedUserId) return;
    setPreviewBusy(true);
    try {
      const { data } = await api.get(`/digest/preview/${selectedUserId}`);
      setPreviewData(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setPreviewBusy(false);
    }
  };

  useEffect(() => {
    if (selectedUserId) doPreview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  const sendNow = async () => {
    if (!window.confirm("Send the Week Ahead digest to every active teammate now?")) return;
    setSendBusy(true);
    try {
      const { data } = await api.post("/digest/send-now");
      setLastResult(data);
      toast.success(`Digest sent to ${data.sent} teammate${data.sent === 1 ? "" : "s"}.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSendBusy(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl" data-testid="digest-page">
      <div>
        <p className="text-xs uppercase tracking-widest text-terracotta-500 font-medium">
          People Operations
        </p>
        <h1 className="text-4xl font-serif text-forest-900 mt-1">MyPatria Week Ahead</h1>
        <p className="mt-2 text-charcoal-soft max-w-2xl">
          A warm Monday-morning digest: one scripture verse, one LIFT nudge, one Training
          reminder, and a reassurance from the ER Center — sent to every active teammate.
        </p>
      </div>

      <section className="patria-card p-6" data-testid="digest-preview-panel">
        <h3 className="text-2xl font-serif text-forest-900 flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-terracotta-500" strokeWidth={1.5} />
          Preview For A Teammate
        </h3>
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <label className="patria-label">Teammate</label>
            <select
              className="patria-input mt-2 w-full"
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              data-testid="digest-user-select"
            >
              {users.map((u) => (
                <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
              ))}
            </select>
          </div>
          <button
            onClick={doPreview}
            disabled={!selectedUserId || previewBusy}
            className="patria-btn-secondary"
            data-testid="digest-preview-btn"
          >
            Refresh Preview
          </button>
        </div>

        {previewData && (
          <div className="mt-6 space-y-3" data-testid="digest-preview-content">
            {previewData.verse && (
              <div className="patria-card p-4 border-l-4 border-forest-600">
                <p className="text-xs uppercase tracking-widest text-charcoal-muted">Scripture Verse</p>
                <p className="mt-1 italic text-charcoal">&ldquo;{previewData.verse}&rdquo;</p>
              </div>
            )}
            {!previewData.verse && (
              <div className="patria-card p-3 text-xs text-charcoal-muted italic">
                No verse — verses toggle may be off or the seeded list is empty.
              </div>
            )}
            <PreviewBlock
              label="LIFT Nudge"
              text={previewData.lift}
              muted="No open LIFT touch-points this week — enjoy the quiet."
            />
            <PreviewBlock
              label="Training Reminder"
              text={previewData.training}
              muted="No training due within the next 7 days."
            />
            <div className="patria-card p-4 border-l-4 border-terracotta-500">
              <p className="text-xs uppercase tracking-widest text-charcoal-muted">
                ER Center Reassurance
              </p>
              <p className="mt-1 text-charcoal">{previewData.er}</p>
            </div>
          </div>
        )}
      </section>

      <section className="patria-card p-6" data-testid="digest-send-panel">
        <h3 className="text-2xl font-serif text-forest-900 flex items-center gap-2">
          <Mail className="h-5 w-5 text-forest-600" strokeWidth={1.5} />
          Send Digest Now
        </h3>
        <p className="mt-2 text-sm text-charcoal-soft max-w-2xl">
          The scheduler fires automatically on Monday mornings. You can also send it now for
          testing or a mid-week refresh — outbound emails write to the mock outbox and every
          teammate receives an in-app notification.
        </p>
        <div className="mt-4 flex justify-end">
          <button
            onClick={sendNow}
            disabled={sendBusy}
            className="patria-btn-primary"
            data-testid="digest-send-now-btn"
          >
            <Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} />
            {sendBusy ? "Sending…" : "Send Week Ahead Digest Now"}
          </button>
        </div>

        {lastResult && (
          <div className="mt-5 patria-card p-4 bg-forest-600/[.05]" data-testid="digest-result">
            <p className="font-medium text-forest-900">
              Delivered {lastResult.sent} • Errors {lastResult.errors}
            </p>
            <p className="text-xs text-charcoal-muted mt-1">
              Week of {lastResult.week_of}
            </p>
          </div>
        )}

        <p className="text-xs text-charcoal-muted italic mt-6">
          Anonymous ER reporters never receive emails — they aren&apos;t users and
          this rule is enforced regardless of digest configuration.
        </p>
      </section>
    </div>
  );
}

function PreviewBlock({ label, text, muted }) {
  return (
    <div className="patria-card p-4">
      <p className="text-xs uppercase tracking-widest text-charcoal-muted">{label}</p>
      {text ? (
        <p className="mt-1 text-charcoal">{text}</p>
      ) : (
        <p className="mt-1 text-sm text-charcoal-muted italic">{muted}</p>
      )}
    </div>
  );
}
